import React from "react";
import styled from "styled-components";
import { withTranslation, WithTranslation } from "react-i18next";
import { Element } from "react-scroll";

import { H2, H1Pre, Body1Pre, Button } from "src/components/Typography";
import { colors } from "src/styles/colors";
import images from "src/images";
import { translate } from "src/locales";
import { media } from "src/utils/media";
import { gTagEvent, ActionType, CategoryType } from "src/configs/analytics";
import TypeformButton from "src/components/TypeformButton";
import { getTalkSalesFormUri } from "src/utils/uri";

type Props = WithTranslation;

const Container = styled(Element)`
  display: flex;
  background-color: ${colors.bg.white.main};
  min-width: 1440px;
  padding-top: calc(74px + 96px);
  ${media.mobile`
    min-width: auto;
    padding-top: calc(56px + 64px);
  `}
`;

const Inner = styled.div`
  display: flex;
  flex-direction: row;
  width: 1360px;
  margin: auto;
  ${media.mobile`
    flex-direction: column;
    width: 100%;
  `}
`;

const LeftGroup = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
  margin-top: 78px;
  ${media.mobile`
    align-items: center;
    padding-bottom: 72px;
    margin-top: 0px;
  `}
`;

const InnerLeftGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-left: 40px;
  ${media.mobile`
    align-items: center;
    width: auto;
    padding-left: 24px;
    padding-right: 24px;
  `}
`;

const Title = styled(H1Pre)`
  color: ${colors.text.dark.title};
  ${media.mobile`
    text-align: center;
  `}
`;

const Description = styled(Body1Pre)`
  color: ${colors.text.dark.body};
  ${media.mobile`
    max-width: 270px;
    text-align: center;
    white-space: normal;
  `}
  margin-top: 32px;
`;

const ConfirmButton = styled(TypeformButton)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 221px;
  height: 72px;
  color: #ffffff;
  background: ${colors.primary.default};
  box-shadow: 0px 10px 40px rgba(54, 62, 76, 0.05);
  border-radius: 100px;
  margin-top: 48px;
  transition: all 0.15s linear 0s;
  cursor: pointer;
  &:hover {
    background: ${colors.primary.hover};
  }
  ${media.mobile`
    width: 165px;
    height: 54px;
    margin-top: 28px;
  `}
`;

const RightGroup = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: flex-end;
  height: 600px;
  ${media.mobile`
    width: 100%;
    height: auto;
  `}
`;

const InnerRightGroup = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 600px;
  ${media.mobile`
    width: 100%;
    height: auto;
    align-items: center;
  `}
`;

const ServiceImage = styled.img`
  width: 551px;
  height: 600px;
  ${media.mobile`
    position: static;
    width: 100%;
    height: auto;
    object-fit: contain;
  `}
`;

const Service2Image = styled.img`
  position: absolute;
  bottom: 50px;
  left: -109px;
  width: 440px;
  height: 260px;
  box-shadow: 0px 28px 40px rgb(7 7 8 / 0.2);
  border-radius: 6px;
  ${media.mobile`
    bottom: -60px;
    left: -8px;
    width: 338px;
    height: auto;
    min-height: 200px;
    box-shadow: 0px 10px 20px rgb(7 7 8 / 0.2);
  `}
`;

const ServiceSection = (props: Props) => {
  const { i18n, t } = props;
  const isKo = i18n.language === "ko";

  const onLink = () => {
    // TODO
  };

  return (
    <Container id="service" name="service">
      <Inner>
        <LeftGroup>
          <InnerLeftGroup>
            <Title>{translate(["serviceSection", "title"], t)}</Title>
            <Description>
              {translate(["serviceSection", "description"], t)}
            </Description>
            <ConfirmButton
              typeformUri={getTalkSalesFormUri(isKo)}
              onClick={onLink}
            >
              {translate(["serviceSection", "confirmButton"], t)}
            </ConfirmButton>
          </InnerLeftGroup>
        </LeftGroup>
        <RightGroup>
          <InnerRightGroup>
            <ServiceImage
              src={isKo ? images.serviceBgSrc : images.serviceEnBgSrc}
              srcSet={isKo ? images.serviceBg : images.serviceEnBg}
            />
            <Service2Image
              src={images.serviceBg2Src}
              srcSet={images.serviceBg2}
            />
          </InnerRightGroup>
        </RightGroup>
      </Inner>
    </Container>
  );
};

export default withTranslation()(ServiceSection);
